import BaseService from '../commons/BaseService';
import { eEthereumTxType, } from '../commons/types';
import { UniswapV3Leverage__factory } from './typechain/UniswapV3Leverage__factory';
import { CLLeveragedPosition__factory } from './typechain/CLLeveragedPosition__factory';
import { ERC721Service } from '../erc721-contract';
export class UniswapV3Leverage extends BaseService {
    constructor(provider, leverageContractAddress, flashloanProviderAddress, assetConverterAddress, uniswapPositionManager) {
        super(provider, UniswapV3Leverage__factory);
        this.leverageContractAddress = leverageContractAddress !== null && leverageContractAddress !== void 0 ? leverageContractAddress : '';
        this.flashloanProviderAddress = flashloanProviderAddress !== null && flashloanProviderAddress !== void 0 ? flashloanProviderAddress : '';
        this.assetConverterAddress = assetConverterAddress !== null && assetConverterAddress !== void 0 ? assetConverterAddress : '';
        this.uniswapPositionManager = uniswapPositionManager !== null && uniswapPositionManager !== void 0 ? uniswapPositionManager : '';
        this.erc721Service = new ERC721Service(provider);
    }
    /**
     * Leverage position
     * @param {string} user - Address of the user
     * @param {string} token - Token which the user is spending
     * @param {string} tokenId - Token id to transfer
     * @param {string} maxAllowedSlippage - Max allowed slippage
     * @returns {LPReserveData} LPReserveData
     */
    leverage({ user, tokenId, maxSwapSlippage, tokenToBorrow, amountToBorrow }) {
        const initParams = { amountToBorrow: amountToBorrow, tokenId: tokenId, maxSwapSlippage: maxSwapSlippage, tokenToBorrow: tokenToBorrow, owner: user, flashLoanProvider: this.flashloanProviderAddress, assetConverter: this.assetConverterAddress };
        const posInterface = CLLeveragedPosition__factory.createInterface();
        // Encode function data and strip off the 0xselector because we only need encoded struct
        // TODO: There has to be a better way to do this
        const encodedParams = '0x' + posInterface.encodeFunctionData('initialize', [initParams]).slice(10);
        const leverageContract = this.getContractInstance(this.leverageContractAddress);
        let tx = this.erc721Service.transfer({ user: user, token: this.uniswapPositionManager, tokenId: tokenId, to: leverageContract.address, params: encodedParams });
        tx.txType = eEthereumTxType.LEVERAGE;
        return tx;
    }
    /**
     * Leverage position
     * @param {string} user - Address of the user
     * @param {string} token - Token which the user is spending
     * @param {string} tokenId - Token id to transfer
     * @param {string} maxAllowedSlippage - Max allowed slippage
     * @returns {LPReserveData} LPReserveData
     */
    leverageTxData({ user, tokenId, maxSwapSlippage, tokenToBorrow, amountToBorrow }) {
        const initParams = { amountToBorrow: amountToBorrow, tokenId: tokenId, maxSwapSlippage: maxSwapSlippage, tokenToBorrow: tokenToBorrow, owner: user, flashLoanProvider: this.flashloanProviderAddress, assetConverter: this.assetConverterAddress };
        const posInterface = CLLeveragedPosition__factory.createInterface();
        // Encode function data and strip off the 0xselector because we only need encoded struct
        // TODO: There has to be a better way to do this
        const encodedParams = '0x' + posInterface.encodeFunctionData('initialize', [initParams]).slice(10);
        const leverageContract = this.getContractInstance(this.leverageContractAddress);
        return this.erc721Service.transferTxData({ user: user, token: this.uniswapPositionManager, tokenId: tokenId, to: leverageContract.address, params: encodedParams });
    }
    deleverage({ user, position, maxSwapSlippage, withdrawLiquidity }) {
        const positionContract = CLLeveragedPosition__factory.connect(position, this.provider);
        const txCallback = this.generateTxCallback({
            rawTxMethod: async () => positionContract.populateTransaction.deleverage(this.flashloanProviderAddress, { receiver: user, maxSwapSlippage: maxSwapSlippage, assetConverter: this.assetConverterAddress, withdrawLiquidity: withdrawLiquidity }),
            from: user,
        });
        return {
            tx: txCallback,
            txType: eEthereumTxType.DELEVERAGE,
            gas: this.generateTxPriceEstimation([], txCallback),
        };
    }
    deleverageTxData({ user, position, maxSwapSlippage, withdrawLiquidity }) {
        const tx = {};
        const txData = CLLeveragedPosition__factory.createInterface().encodeFunctionData('deleverage', [this.flashloanProviderAddress, { receiver: user, maxSwapSlippage: maxSwapSlippage, assetConverter: this.assetConverterAddress, withdrawLiquidity: withdrawLiquidity }]);
        tx.data = txData;
        tx.to = position;
        tx.from = user;
        return tx;
    }
    compound({ user, position, maxSwapSlippage }) {
        const positionContract = CLLeveragedPosition__factory.connect(position, this.provider);
        const txCallback = this.generateTxCallback({
            rawTxMethod: async () => positionContract.populateTransaction.compound(this.flashloanProviderAddress, { maxSwapSlippage: maxSwapSlippage, assetConverter: this.assetConverterAddress }),
            from: user,
        });
        return {
            tx: txCallback,
            txType: eEthereumTxType.DELEVERAGE,
            gas: this.generateTxPriceEstimation([], txCallback),
        };
    }
    compoundTxData({ user, position, maxSwapSlippage }) {
        const tx = {};
        const txData = CLLeveragedPosition__factory.createInterface().encodeFunctionData('compound', [this.flashloanProviderAddress, { maxSwapSlippage: maxSwapSlippage, assetConverter: this.assetConverterAddress }]);
        tx.data = txData;
        tx.to = position;
        tx.from = user;
        return tx;
    }
    rebalance({ user, position, maxSwapSlippage, newTickLower, newTickUpper }) {
        const positionContract = CLLeveragedPosition__factory.connect(position, this.provider);
        const txCallback = this.generateTxCallback({
            rawTxMethod: async () => positionContract.populateTransaction.rebalance(this.flashloanProviderAddress, { maxSwapSlippage: maxSwapSlippage, assetConverter: this.assetConverterAddress, newTickLower: newTickLower, newTickUpper: newTickUpper }),
            from: user,
        });
        return {
            tx: txCallback,
            txType: eEthereumTxType.DELEVERAGE,
            gas: this.generateTxPriceEstimation([], txCallback),
        };
    }
    rebalanceTxData({ user, position, maxSwapSlippage, newTickLower, newTickUpper }) {
        const tx = {};
        const txData = CLLeveragedPosition__factory.createInterface().encodeFunctionData('rebalance', [this.flashloanProviderAddress, { maxSwapSlippage: maxSwapSlippage, assetConverter: this.assetConverterAddress, newTickLower: newTickLower, newTickUpper: newTickUpper }]);
        tx.data = txData;
        tx.to = position;
        tx.from = user;
        return tx;
    }
    claimFeesTxData({ user, position, maxSwapSlippage }) {
        const tx = {};
        const txData = CLLeveragedPosition__factory.createInterface().encodeFunctionData('claimFees', [this.flashloanProviderAddress, { maxSwapSlippage: maxSwapSlippage, assetConverter: this.assetConverterAddress, withdrawFees: true }]);
        tx.data = txData;
        tx.to = position;
        tx.from = user;
        return tx;
    }
}
//# sourceMappingURL=index.js.map